import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { findDoc, isTabbed } from '../config/manifest';
import type { Heading } from '../types/doc';
import DocBreadcrumb from './DocBreadcrumb';
import DocHeader from './DocHeader';
import DocTabStrip from './DocTabStrip';
import DocBody, { splitHeadings } from './DocBody';
import DocTOC from './DocTOC';
import DocPrevNext from './DocPrevNext';

interface FetchState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  html?: string;
  headings?: Heading[];
  errorMessage?: string;
  errorStatus?: number;
}

// Underscore added to align with substrate's api_name policy
// (scripts/pack_schema.py _API_NAME_CHARSET_RE = [a-z][a-z0-9_]*). Without
// the underscore, ~22 schema groups/slugs like attack_technique, ai_model,
// engagement_scope, scope_token, schema-attack_graph hit the
// `!slugSegmentsOk → Navigate to /` branch in DocView and silently
// redirect to home. SANDWICH PRE-CODE 2026-05-23 on substrate#226.
const SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export default function DocView() {
  const params = useParams<{ group: string; slug: string; tab?: string }>();
  const groupId = params.group || '';
  const slug = params.slug || '';
  const tabParam = params.tab;

  const found = useMemo(() => findDoc(groupId, slug), [groupId, slug]);
  const { getIdToken, logout } = useAuth();
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'idle' });

  // Resolve target tab. For tabbed docs missing :tab, redirect to defaultTab.
  // For single docs with a stray :tab, redirect to canonical URL.
  const tabbed = found ? isTabbed(found.doc) : false;
  const activeTab = useMemo(() => {
    if (!found || !tabbed) return undefined;
    const tabs = (found.doc as { tabs: { id: string; label: string; source: string }[] }).tabs;
    if (tabParam && tabs.some((t) => t.id === tabParam)) return tabs.find((t) => t.id === tabParam)!;
    return tabs.find((t) => t.id === (found.doc as { defaultTab: string }).defaultTab) || tabs[0];
  }, [found, tabbed, tabParam]);

  // Slug-shape sanity (the manifest is trusted, but be defensive).
  const slugSegmentsOk =
    SLUG_RE.test(groupId) &&
    SLUG_RE.test(slug) &&
    (!tabParam || SLUG_RE.test(tabParam));

  useEffect(() => {
    if (!found || !slugSegmentsOk) return;
    let cancelled = false;
    setFetchState({ status: 'loading' });

    (async () => {
      try {
        const token = await getIdToken();
        if (!token) throw new Error('Not signed in.');

        const url = tabbed
          ? `/api/content/${encodeURIComponent(groupId)}/${encodeURIComponent(slug)}/${encodeURIComponent(activeTab!.id)}`
          : `/api/content/${encodeURIComponent(groupId)}/${encodeURIComponent(slug)}`;

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'omit',
        });
        if (cancelled) return;
        if (!res.ok) {
          setFetchState({
            status: 'error',
            errorStatus: res.status,
            errorMessage:
              res.status === 401 || res.status === 403
                ? 'Your session is not authorized for this content.'
                : `Failed to load (HTTP ${res.status}).`,
          });
          return;
        }
        const raw = await res.text();
        if (cancelled) return;
        const { headings, html } = splitHeadings(raw);
        setFetchState({ status: 'ready', html, headings });
        // Anchor scroll if URL has hash.
        if (window.location.hash) {
          requestAnimationFrame(() => {
            const target = document.getElementById(window.location.hash.slice(1));
            if (target) target.scrollIntoView({ behavior: 'auto', block: 'start' });
          });
        } else {
          window.scrollTo({ top: 0 });
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load.';
        setFetchState({ status: 'error', errorMessage: message });
      }
    })();

    return () => { cancelled = true; };
  }, [found, slugSegmentsOk, groupId, slug, tabbed, activeTab, getIdToken]);

  if (!slugSegmentsOk || !found) return <Navigate to="/" replace />;

  // Tabbed doc with missing/invalid :tab → canonical default.
  if (tabbed && (!tabParam || tabParam !== activeTab?.id)) {
    return <Navigate to={`/d/${groupId}/${slug}/${activeTab!.id}`} replace />;
  }
  // Single doc with a stray :tab → canonical no-tab URL.
  if (!tabbed && tabParam) {
    return <Navigate to={`/d/${groupId}/${slug}`} replace />;
  }

  return (
    <div className="doc-view">
      <DocBreadcrumb group={found.group} doc={found.doc} tab={activeTab} />
      <DocHeader group={found.group} doc={found.doc} />
      {tabbed && activeTab && (
        <DocTabStrip
          groupId={groupId}
          slug={slug}
          tabs={(found.doc as { tabs: { id: string; label: string; source: string }[] }).tabs}
          activeTabId={activeTab.id}
        />
      )}

      {fetchState.status === 'loading' && (
        <div className="doc-view__loading" aria-live="polite">Loading document…</div>
      )}

      {fetchState.status === 'error' && (
        <ErrorPanel
          message={fetchState.errorMessage || 'Failed to load.'}
          status={fetchState.errorStatus}
          onSignOut={logout}
        />
      )}

      {fetchState.status === 'ready' && fetchState.html !== undefined && (
        <div className="doc-view__panel">
          <div className="doc-view__content">
            <DocBody html={fetchState.html} />
            <DocPrevNext groupId={groupId} slug={slug} />
          </div>
          <DocTOC headings={fetchState.headings || []} />
        </div>
      )}
    </div>
  );
}

function ErrorPanel({
  message,
  status,
  onSignOut,
}: {
  message: string;
  status?: number;
  onSignOut: () => void;
}) {
  const showSignOut = status === 401 || status === 403;
  return (
    <div className="doc-view__error">
      <div className="doc-view__error-head">
        <AlertTriangle className="h-4 w-4" />
        <span>{status ? `Error ${status}` : 'Error'}</span>
      </div>
      <p>{message}</p>
      {showSignOut && (
        <button onClick={onSignOut} className="doc-view__error-btn">
          Sign out and try again
        </button>
      )}
    </div>
  );
}
